/** 매칭되지 않은 PO 품목명 → Item DB 매핑 후 ItemMapping 저장.
 *  저장 성공 시 상위(ReviewPanel)의 onMapped 콜백으로 로컬 orderRows 갱신 → 해당 라인이 매칭 상태로 전환됨.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Link2, Save } from 'lucide-react'

import { api } from '@/api/client'
import { useToast } from '@/stores/toast'
import { ItemPicker } from '@/components/ItemPicker'
import type { Item } from '@/types/models'
import { cn } from '@/lib/utils'

interface Props {
  items: string[]                          // 미매칭 원본 품목명
  itemCodes?: Record<string, string>       // item_name → PO 원본코드
  groupId: number | null
  groupName: string | null
  onMapped: (itemName: string, matchedItem: Item) => void
}

export function MissingItemsMapper({
  items, itemCodes = {}, groupId, groupName, onMapped,
}: Props) {
  const push = useToast((s) => s.push)
  const [picks, setPicks] = useState<Record<string, Item>>({})
  const [saved, setSaved] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<Set<string>>(new Set())

  const tableRef = useRef<HTMLTableElement>(null)
  const saveAllBtnRef = useRef<HTMLButtonElement>(null)
  const picksRef = useRef(picks); picksRef.current = picks
  const savedRef = useRef(saved); savedRef.current = saved

  function focusRowPicker(idx: number) {
    requestAnimationFrame(() => {
      const btn = tableRef.current?.querySelector<HTMLElement>(
        `tr[data-row-idx="${idx}"] [data-field="item-trigger"] button`,
      )
      btn?.focus()
    })
  }
  function focusNextTodo(fromIdx: number = -1) {
    requestAnimationFrame(() => {
      // 다음 미선택/미저장 행 찾기
      for (let i = fromIdx + 1; i < items.length; i++) {
        const n = items[i]
        if (!savedRef.current.has(n)) return focusRowPicker(i)
      }
      for (let i = 0; i < items.length; i++) {
        const n = items[i]
        if (!savedRef.current.has(n)) return focusRowPicker(i)
      }
      // 다 저장됨 → saveAll 버튼
      saveAllBtnRef.current?.focus()
    })
  }

  // 마운트 시 첫 행 자동 focus
  useEffect(() => {
    if (items.length > 0) focusNextTodo(-1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  const targetLabel = `상위 "${groupName ?? '(미지정)'}"`

  async function saveOne(itemName: string) {
    const item = picks[itemName]
    if (!item) return
    if (!groupId) {
      push('상위 거래처가 선택되지 않았습니다', 'warn'); return
    }
    setBusy((cur) => new Set([...cur, itemName]))
    try {
      await api('/api/item-mappings/', {
        method: 'POST',
        body: {
          source_code: itemCodes[itemName] ?? '',
          source_name: itemName,
          item: item.id,
          group: groupId, partner: null,
        },
      })
      setSaved((cur) => new Set([...cur, itemName]))
      onMapped(itemName, item)
      push(`매핑 저장: ${itemName} → ${item.name}`, 'success')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      push(`매핑 저장 실패 (${itemName}): ${msg}`, 'error', 6000)
    } finally {
      setBusy((cur) => {
        const next = new Set(cur); next.delete(itemName); return next
      })
    }
  }

  async function saveAll() {
    const targets = items.filter((n) => picks[n] && !saved.has(n))
    for (const n of targets) {
      // eslint-disable-next-line no-await-in-loop
      await saveOne(n)
    }
  }

  function onTableKeyDown(e: React.KeyboardEvent) {
    // Ctrl/Cmd + Enter → saveAll
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      if (items.filter(n => picks[n] && !saved.has(n)).length > 0) saveAll()
      return
    }
    const el = e.target instanceof HTMLElement ? e.target : null
    if (!el || !tableRef.current?.contains(el)) return
    const trEl = el.closest<HTMLElement>('[data-row-idx]')
    const idxStr = trEl?.getAttribute('data-row-idx')
    if (!idxStr) return
    const currentI = parseInt(idxStr, 10)

    const isPickerBtn = el.tagName === 'BUTTON' && !!el.closest('[data-field="item-trigger"]')
    if (!isPickerBtn) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (currentI + 1 < items.length) focusRowPicker(currentI + 1)
      else saveAllBtnRef.current?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (currentI > 0) focusRowPicker(currentI - 1)
    }
  }

  const readyToSave = items.filter((n) => picks[n] && !saved.has(n)).length
  const pending = items.filter((n) => !saved.has(n))

  return (
    <div className="mx-4 mt-3 rounded-lg bg-white border border-rose-300 p-3">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="text-xs font-semibold text-rose-800">
          📦 매핑되지 않은 품목 ({pending.length}종)
        </div>
        <span className="text-xs text-slate-500">품목 선택 후 저장 → 코드 매칭 등록 · 다음 변환부터 자동 매칭</span>
        <div className="flex-1"/>
        <span className="text-[11px] text-slate-500">→ {targetLabel}</span>
        <button ref={saveAllBtnRef}
                onClick={saveAll}
                disabled={readyToSave === 0}
                className={cn(
                  'text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 focus:ring-2 focus:ring-emerald-300 focus:outline-none',
                  readyToSave === 0
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                )}>
          <Save className="w-3.5 h-3.5"/> 선택된 항목 저장 ({readyToSave})
        </button>
      </div>
      <div className="text-[10px] text-slate-500 mb-1">
        단축키: <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">↑↓</kbd> 행 이동 ·
        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Enter</kbd> 품목 선택 · 선택 후 <span className="font-semibold">자동으로 다음 행</span> ·
        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Ctrl+Enter</kbd> 일괄 저장
      </div>

      <div className="border border-slate-200 rounded overflow-hidden max-h-72 overflow-y-auto">
        <table ref={tableRef} onKeyDown={onTableKeyDown} className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left font-medium">PO 원본품명</th>
              <th className="px-3 py-2 text-center w-8">→</th>
              <th className="px-3 py-2 text-left font-medium w-72">매핑할 품목</th>
              <th className="px-3 py-2 text-center w-24">상태/저장</th>
            </tr>
          </thead>
          <tbody>
            {items.map((name, idx) => {
              const item = picks[name]
              const isSaved = saved.has(name)
              const isBusy = busy.has(name)
              return (
                <tr key={name} data-row-idx={idx} className={cn(
                  'border-t border-slate-100',
                  isSaved ? 'bg-emerald-50/40' : 'bg-white hover:bg-slate-50',
                )}>
                  <td className="px-3 py-1.5 text-slate-800">{name}</td>
                  <td className="px-3 py-1.5 text-center text-slate-300"><Link2 className="w-3.5 h-3.5 inline"/></td>
                  <td className="px-3 py-1.5" data-field="item-trigger">
                    <ItemPicker
                      value={item?.id ?? null}
                      onChange={(it) => {
                        setPicks((cur) => ({ ...cur, [name]: it }))
                        // 선택 후 다음 미저장 행으로 자동 이동
                        focusNextTodo(idx)
                      }}
                      placeholder="품목 선택..."
                    />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {isSaved ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3"/> 저장됨
                      </span>
                    ) : (
                      <button
                        onClick={() => saveOne(name)}
                        disabled={!item || isBusy}
                        className={cn(
                          'text-[11px] font-semibold rounded px-2 py-0.5',
                          (!item || isBusy)
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                        )}>
                        {isBusy ? '...' : '저장'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
